import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { inputStyle, labelStyle } from "./quotationDetailShared";

type QuotationAutocompleteFieldProps<T> = {
  label: string;
  value: string;
  placeholder: string;
  selected: boolean;
  readOnly?: boolean;
  showDropdown: boolean;
  hideDropdown?: boolean;
  options: T[];
  getKey: (item: T) => string;
  getLabel: (item: T) => string;
  getMeta?: (item: T) => string;
  createLabel: string;
  onValueChange: (value: string) => void;
  onClear: () => void;
  onFocus: () => void;
  onBlur: () => void;
  onDismiss: () => void;
  onSelect: (item: T) => void;
  onCreateNew: () => void;
};

const dropdownStyle: CSSProperties = {
  background: "var(--login-surface)",
  border: "1px solid var(--login-border)",
  borderRadius: "0.5rem",
};

export function QuotationAutocompleteField<T>({
  label,
  value,
  placeholder,
  selected,
  readOnly = false,
  showDropdown,
  hideDropdown = false,
  options,
  getKey,
  getLabel,
  getMeta,
  createLabel,
  onValueChange,
  onClear,
  onFocus,
  onBlur,
  onDismiss,
  onSelect,
  onCreateNew,
}: QuotationAutocompleteFieldProps<T>) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const lastPointerType = useRef("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    maxHeight: 0,
  });
  const isOpen = showDropdown && !hideDropdown && !readOnly;
  const listId = `${inputId}-options`;
  const dialog = inputRef.current?.closest<HTMLElement>(
    ".quotation-item-editor",
  );
  useLayoutEffect(() => {
    if (!isOpen) return;
    function positionList() {
      const rect = inputRef.current?.getBoundingClientRect();
      const list = listRef.current;
      if (!rect || !list) return;
      const edge = 8;
      const gap = 6;
      const width = Math.min(rect.width, window.innerWidth - edge * 2);
      list.style.width = `${width}px`;
      // scrollHeight rounds fractional rows; account for the list border.
      const height = Math.min(list.scrollHeight + 1.5, 240);
      const below = Math.max(0, window.innerHeight - rect.bottom - gap - edge);
      const above = Math.max(0, rect.top - gap - edge);
      const placeAbove = below < height && above > below;
      const maxHeight = Math.min(height, placeAbove ? above : below);
      setPosition({
        top: placeAbove
          ? Math.max(edge, rect.top - gap - maxHeight)
          : Math.min(rect.bottom + gap, window.innerHeight - edge),
        left: Math.min(
          Math.max(edge, rect.left),
          window.innerWidth - edge - width,
        ),
        width,
        maxHeight,
      });
    }
    positionList();
    window.addEventListener("scroll", positionList, true);
    window.addEventListener("resize", positionList);
    return () => {
      window.removeEventListener("scroll", positionList, true);
      window.removeEventListener("resize", positionList);
    };
  }, [isOpen, options.length]);

  useEffect(() => {
    if (!isOpen) return;
    function closeOutside(event: PointerEvent) {
      if (
        event.target instanceof Node &&
        !inputRef.current?.contains(event.target) &&
        !listRef.current?.contains(event.target)
      ) {
        lastPointerType.current = "";
        onDismiss();
      }
    }
    document.addEventListener("pointerdown", closeOutside, true);
    return () =>
      document.removeEventListener("pointerdown", closeOutside, true);
  }, [isOpen, onDismiss]);

  useLayoutEffect(() => {
    if (isOpen && activeIndex >= 0) {
      const list = listRef.current;
      const option = list?.children[activeIndex];
      if (list && option) {
        const listBox = list.getBoundingClientRect();
        const optionBox = option.getBoundingClientRect();
        if (optionBox.bottom > listBox.bottom)
          list.scrollTop += optionBox.bottom - listBox.bottom;
        if (optionBox.top < listBox.top)
          list.scrollTop -= listBox.top - optionBox.top;
      }
    }
  }, [activeIndex, isOpen]);
  const selectIndex = (index: number) => {
    setActiveIndex(-1);
    if (index === options.length) onCreateNew();
    else if (index >= 0) onSelect(options[index]!);
  };
  const selectOnPointerDown = (event: ReactPointerEvent, index: number) => {
    lastPointerType.current = event.pointerType;
    if (event.pointerType === "touch") return;
    event.preventDefault();
    selectIndex(index);
  };
  const selectOnTouchClick = (index: number) => {
    if (lastPointerType.current !== "touch") return;
    lastPointerType.current = "";
    selectIndex(index);
  };
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "0.375rem",
      }}
    >
      {readOnly ? (
        <span style={labelStyle}>{label}</span>
      ) : (
        <label htmlFor={inputId} style={labelStyle}>
          {label}
        </label>
      )}
      <div style={{ position: "relative" }}>
        {readOnly ? (
          <strong className="quotation-editor-product-value">{value}</strong>
        ) : (
          <input
            ref={inputRef}
            id={inputId}
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={isOpen}
            aria-controls={isOpen ? listId : undefined}
            aria-activedescendant={
              isOpen && activeIndex >= 0
                ? `${inputId}-option-${activeIndex}`
                : undefined
            }
            className="autocomplete-input"
            style={{ ...inputStyle, paddingRight: "3rem" }}
            value={value}
            onChange={(event) => {
              setActiveIndex(-1);
              onValueChange(event.target.value);
            }}
            onKeyDown={(event) => {
              if (readOnly) return;
              if (event.key === "Tab") {
                lastPointerType.current = "";
                if (isOpen) onDismiss();
                return;
              }
              if (!isOpen) {
                if (
                  !hideDropdown &&
                  (event.key === "ArrowDown" || event.key === "ArrowUp")
                ) {
                  event.preventDefault();
                  setActiveIndex(
                    event.key === "ArrowDown" ? 0 : options.length,
                  );
                  onFocus();
                }
                return;
              }
              if (event.key === "Escape") {
                event.preventDefault();
                event.stopPropagation();
                lastPointerType.current = "";
                setActiveIndex(-1);
                onDismiss();
              } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                event.preventDefault();
                const count = options.length + 1;
                setActiveIndex((index) =>
                  index < 0
                    ? event.key === "ArrowDown"
                      ? 0
                      : count - 1
                    : (index + (event.key === "ArrowDown" ? 1 : count - 1)) %
                      count,
                );
              } else if (event.key === "Enter") {
                event.preventDefault();
                event.stopPropagation();
                selectIndex(activeIndex);
              }
            }}
            onFocus={() => {
              if (!readOnly) onFocus();
            }}
            onClick={() => {
              if (!readOnly && !hideDropdown) onFocus();
            }}
            onBlur={() => {
              // Touch tap blurs the input before its click; keep the option mounted.
              if (lastPointerType.current !== "touch") onBlur();
            }}
            placeholder={placeholder}
          />
        )}
        {value && !readOnly ? (
          <button
            type="button"
            onMouseDown={(event) => {
              event.preventDefault();
              onClear();
            }}
            style={{
              position: "absolute",
              right: selected ? "2rem" : "0.75rem",
              top: "50%",
              transform: "translateY(-50%)",
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
              display: "flex",
              color: "var(--login-muted)",
              zIndex: 10,
            }}
          >
            <X size={16} />
          </button>
        ) : null}
        {selected && !readOnly ? (
          <span
            style={{
              position: "absolute",
              right: "0.75rem",
              top: "50%",
              transform: "translateY(-50%)",
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              background: "#16a34a",
            }}
          />
        ) : null}
        {isOpen && dialog
          ? createPortal(
              <div
                ref={listRef}
                id={listId}
                role="listbox"
                className="quotation-autocomplete-list"
                style={{ ...dropdownStyle, ...position }}
              >
                {options.map((option, index) => (
                  <div
                    key={getKey(option)}
                    id={`${inputId}-option-${index}`}
                    className="dropdown-item"
                    role="option"
                    aria-selected={activeIndex === index}
                    onPointerDown={(event) => selectOnPointerDown(event, index)}
                    onTouchStart={() => (lastPointerType.current = "touch")}
                    onPointerCancel={() => (lastPointerType.current = "")}
                    onClick={() => selectOnTouchClick(index)}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      ...(activeIndex === index
                        ? { background: "var(--login-bg)" }
                        : {}),
                    }}
                  >
                    <span
                      style={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: "0.9375rem",
                        color: "var(--login-text)",
                      }}
                    >
                      {getLabel(option)}
                    </span>
                    {getMeta ? (
                      <span
                        style={{
                          fontFamily: "'Barlow Condensed', sans-serif",
                          fontSize: "0.75rem",
                          color: "var(--login-muted)",
                          letterSpacing: "0.05em",
                        }}
                      >
                        {getMeta(option)}
                      </span>
                    ) : null}
                  </div>
                ))}
                <div
                  id={`${inputId}-option-${options.length}`}
                  className="dropdown-item"
                  role="option"
                  aria-selected={activeIndex === options.length}
                  onPointerDown={(event) =>
                    selectOnPointerDown(event, options.length)
                  }
                  onTouchStart={() => (lastPointerType.current = "touch")}
                  onPointerCancel={() => (lastPointerType.current = "")}
                  onClick={() => selectOnTouchClick(options.length)}
                  style={{
                    color: "var(--login-accent)",
                    fontFamily: "'Barlow Condensed', sans-serif",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    letterSpacing: "0.04em",
                    borderTop: "1px solid var(--login-border)",
                    ...(activeIndex === options.length
                      ? { background: "var(--login-bg)" }
                      : {}),
                  }}
                >
                  {createLabel}
                </div>
              </div>,
              dialog,
            )
          : null}
      </div>
    </div>
  );
}
